/**
 * Typed fetch wrapper for the API (docs/API.md).
 *
 * Requests are same-origin in dev (Vite proxies `/api` -> the API, see
 * vite.config.ts) and same-origin in the default production deployment too
 * (Azure Static Web Apps proxies `/api/*` to the API container app as a
 * linked backend). `credentials: 'include'` is therefore always safe to send
 * and is never conditional — and it remains correct for the absolute-URL
 * escape hatch in `runtime-config.ts`, which additionally requires the API to
 * issue `SameSite=None; Secure` cookies. The session is an httpOnly cookie
 * (docs/SECURITY.md §6, BFF pattern) — this file never reads, stores or
 * attaches a bearer token, because there isn't one for the SPA to hold.
 *
 * The base URL is resolved at RUNTIME, not baked in at build time, so one
 * built artifact can be promoted from staging to production unchanged. See
 * `runtime-config.ts` for why.
 */

import { ERROR_CODES, errorEnvelopeSchema, type ErrorCode, type ErrorDetail } from '@ithelpdesk/shared';
import { apiBaseUrl } from './runtime-config';


export interface ApiErrorInit {
  code: ErrorCode;
  status: number;
  message: string;
  requestId: string;
  details?: ErrorDetail[];
}

/**
 * The one shape every failed request throws. Callers switch on `.code`
 * (docs/API.md §2) rather than parsing `.message`, which is user-facing copy
 * and may change wording. `.status` is kept alongside `.code` because two
 * distinct statuses (428 vs 412) can matter even where the handling is
 * similar, and because a non-JSON failure (e.g. a proxy's own error page)
 * still needs a status to fall back on.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string;
  readonly details: ErrorDetail[] | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.details = init.details;
  }
}

/** Status used for a request that never reached the server at all (offline, API not running, CORS). Not a real HTTP status — a sentinel so callers can still branch on `.status`. */
export const NETWORK_ERROR_STATUS = 0;

function fallbackCodeForStatus(status: number): ErrorCode {
  switch (status) {
    case 401:
      return ERROR_CODES.UNAUTHENTICATED;
    case 403:
      return ERROR_CODES.FORBIDDEN;
    case 404:
      return ERROR_CODES.NOT_FOUND;
    case 409:
      return ERROR_CODES.INVALID_TRANSITION;
    case 412:
      return ERROR_CODES.VERSION_CONFLICT;
    case 422:
      return ERROR_CODES.INVALID_CONFIRMATION;
    case 428:
      return ERROR_CODES.PRECONDITION_REQUIRED;
    case 429:
      return ERROR_CODES.RATE_LIMITED;
    case 503:
      return ERROR_CODES.DEPENDENCY_ERROR;
    default:
      return ERROR_CODES.INTERNAL_ERROR;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Sent as `If-Match`. Required by every mutating ticket endpoint (docs/API.md §2) — pass the ETag captured from the prior GET. */
  ifMatch?: string;
  /** Sent as `Idempotency-Key` (docs/API.md §2). Required by the API for POST /tickets, /resolve, /comments, /attachments — generate a fresh UUID per user-initiated submission, not per retry. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export interface RawResponse<T> {
  data: T;
  /** The `ETag` response header, when present (ticket detail GETs). Callers that will later mutate this resource must hold onto it and pass it back as `ifMatch`. */
  etag: string | null;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The single place an HTTP request is made. Everything else in `src/api` is
 * a thin, typed helper over this.
 */
/**
 * Builds the typed error for a non-2xx response whose body (already parsed, or
 * null when it was not JSON) may carry the documented error envelope. The
 * body text is never surfaced when it does not match (docs/SECURITY.md §11).
 */
function apiErrorFromResponse(response: Response, json: unknown): ApiError {
  const parsed = errorEnvelopeSchema.safeParse(json);
  if (parsed.success) {
    return new ApiError({
      code: parsed.data.error.code,
      status: response.status,
      message: parsed.data.error.message,
      requestId: parsed.data.error.requestId,
      ...(parsed.data.error.details ? { details: parsed.data.error.details } : {}),
    });
  }
  // The body didn't match the documented envelope — a proxy error page, an
  // empty 500, etc. Fall back to a status-derived code and a generic message.
  return new ApiError({
    code: fallbackCodeForStatus(response.status),
    status: response.status,
    message: 'Something went wrong. Please try again.',
    requestId: response.headers.get('X-Request-Id') ?? 'unknown',
  });
}

/**
 * Fetches a binary endpoint (file download / export) with the session cookie
 * and returns the raw `Response` for a 2xx; any failure — network or non-2xx,
 * including the standard JSON error envelope (403/400/429/422...) — throws the
 * same typed `ApiError` as `request`. GET only, never mutates.
 */
export async function requestRaw(path: string, options: { signal?: AbortSignal } = {}): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}${path}`, { method: 'GET', credentials: 'include', ...(options.signal ? { signal: options.signal } : {}) });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      code: ERROR_CODES.DEPENDENCY_ERROR,
      status: NETWORK_ERROR_STATUS,
      message: 'Could not reach the server. Check your connection and try again.',
      requestId: 'local',
    });
  }
  if (response.ok) return response;
  let json: unknown = null;
  try {
    json = JSON.parse(await response.text());
  } catch {
    json = null;
  }
  throw apiErrorFromResponse(response, json);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<RawResponse<T>> {
  const method = options.method ?? 'GET';
  const isMutating = MUTATING_METHODS.has(method);
  const headers: Record<string, string> = {};

  // FormData (file uploads) sets its own multipart boundary header.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  // Defence in depth against cross-site form submission (docs/SECURITY.md §8)
  // — a plain HTML form cannot set a custom header, so this alone blocks the
  // classic no-JS CSRF POST even before same-site cookies are considered.
  if (isMutating) headers['X-Requested-With'] = 'ithelpdesk-web';
  if (options.ifMatch !== undefined) headers['If-Match'] = options.ifMatch;
  if (options.idempotencyKey !== undefined) headers['Idempotency-Key'] = options.idempotencyKey;

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}${path}`, {
      method,
      credentials: 'include',
      headers,
      // `RequestInit.body` is `BodyInit | null` (no `| undefined`), so under
      // `exactOptionalPropertyTypes` the key must be left out entirely
      // rather than set to `undefined` for a bodyless GET/DELETE.
      ...(options.body !== undefined ? { body: isFormData ? (options.body as FormData) : JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    // The API may not be running (docs handed to this pass say so explicitly).
    // Never fabricate a response — surface a typed, catchable error instead.
    throw new ApiError({
      code: ERROR_CODES.DEPENDENCY_ERROR,
      status: NETWORK_ERROR_STATUS,
      message: 'Could not reach the server. Check your connection and try again.',
      requestId: 'local',
    });
  }

  const etag = response.headers.get('ETag');

  if (response.status === 204) {
    return { data: undefined as T, etag };
  }

  let json: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (!response.ok) throw apiErrorFromResponse(response, json);

  return { data: json as T, etag };
}

export async function apiGet<T>(path: string, options: { signal?: AbortSignal } = {}): Promise<T> {
  const { data } = await request<T>(path, { method: 'GET', ...options });
  return data;
}

/** Use when the caller needs the ETag too (ticket detail — hold it for the next mutation's `If-Match`). */
export async function apiGetWithEtag<T>(path: string, options: { signal?: AbortSignal } = {}): Promise<RawResponse<T>> {
  return request<T>(path, { method: 'GET', ...options });
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  options: { ifMatch?: string; idempotencyKey?: string; signal?: AbortSignal } = {},
): Promise<T> {
  const { data } = await request<T>(path, { method: 'POST', body, ...options });
  return data;
}

/**
 * For a lifecycle mutation, `ifMatch` is mandatory at the type level — there
 * is no code path in this client that can send a ticket mutation without the
 * ETag from the ticket's last GET.
 */
export async function apiPostVersioned<T>(
  path: string,
  body: unknown,
  ifMatch: string,
  options: { idempotencyKey?: string; signal?: AbortSignal } = {},
): Promise<T> {
  const { data } = await request<T>(path, { method: 'POST', body, ifMatch, ...options });
  return data;
}

/** Idempotent full replacement of a sub-resource (e.g. `PUT /admin/users/:id/role`). */
export async function apiPut<T>(path: string, body: unknown, options: { signal?: AbortSignal } = {}): Promise<T> {
  const { data } = await request<T>(path, { method: 'PUT', body, ...options });
  return data;
}

export async function apiPatch<T>(path: string, body: unknown, ifMatch: string, options: { signal?: AbortSignal } = {}): Promise<T> {
  const { data } = await request<T>(path, { method: 'PATCH', body, ifMatch, ...options });
  return data;
}

/**
 * ============================================================================
 * ON VERSION CONFLICTS — READ BEFORE TOUCHING THIS FILE
 * ============================================================================
 * A `412 VERSION_CONFLICT` means the ETag this client held is stale: someone
 * else changed the ticket since it was fetched. The only correct response is
 * to refetch the ticket and let the *user* decide what to do with the fresh
 * state — never to silently re-send the same mutation with the new version
 * bolted on. An auto-retry-with-bumped-version would resubmit a decision
 * (e.g. "resolve with this text", "confirm this exact resolutionId") against
 * a ticket state the caller never actually saw, which is precisely the
 * re-authorisation step `If-Match` exists to force. There is deliberately no
 * helper anywhere in this module that reads the failed response's current
 * version and retries — do not add one.
 * ============================================================================
 */

/**
 * TanStack Query `retry` predicate shared by every query hook in
 * `queries.ts`/`session.ts`. A 4xx is never retried — it means the request
 * itself was wrong or unauthorized, and hammering it again will not change
 * that. A network failure (`NETWORK_ERROR_STATUS`) or a 5xx gets a couple of
 * attempts, since those can be transient.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) {
    return error.status === NETWORK_ERROR_STATUS || error.status >= 500;
  }
  return false;
}

/** Every lifecycle mutation hook should pass `retry: false` explicitly (TanStack Query mutations already default to no retry, but this keeps the intent visible at each call site rather than relying on a default that could change). */
export const NO_MUTATION_RETRY = false as const;
